import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, Table, Tooltip } from "antd";
import { Spinner } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";
import { FiFilter, FiCalendar, FiRefreshCw, FiTrash2, FiBarChart2, FiCheck, FiChevronDown, FiDownload, FiList, FiTrendingUp, FiTrendingDown } from "react-icons/fi";
import SidebarNav from "../../sidebar";
import CustomPagination from "../../Pagination/pagination";
import {
  getFinanceOverviewApi,
  getFinanceTimeseriesApi,
  getFinanceOverviewTableApi,
  exportFinanceOverviewCsvApi,
} from "../../../../service/api";
import { formatInr, formatUsdt, formatNumber, KpiCard, saveBlobAsFile } from "../shared";

const PRESETS = [
  { label: "This Month", get: () => ({ s: moment().startOf("month"), e: moment().endOf("day") }) },
  { label: "Last 7 Days", get: () => ({ s: moment().subtract(6, "days").startOf("day"), e: moment().endOf("day") }) },
  { label: "Last 30 Days", get: () => ({ s: moment().subtract(29, "days").startOf("day"), e: moment().endOf("day") }) },
  { label: "Year to Date", get: () => ({ s: moment().startOf("year"), e: moment().endOf("day") }) },
];

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TipLabel = ({ label, tip }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    {label}
    <Tooltip title={tip} placement="top">
      <span style={{ cursor: "help", color: "#aab4be", fontSize: 10, border: "1px solid #d9d9d9", borderRadius: "50%", width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>?</span>
    </Tooltip>
  </span>
);

const FinanceOverview = () => {
  const initial = PRESETS[0].get();
  const [startDate, setStartDate] = useState(initial.s.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(initial.e.format("YYYY-MM-DD"));
  const [activePreset, setActivePreset] = useState(PRESETS[0].label);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ov, ts, tbl] = await Promise.all([
        getFinanceOverviewApi(params),
        getFinanceTimeseriesApi(params),
        getFinanceOverviewTableApi({ ...params, page, sizePerPage: pageSize }),
      ]);
      if (ov?.status) setOverview(ov.data);
      if (ts?.status) setSeries(ts.data || []);
      if (tbl?.status) {
        setTableRows(tbl.data?.result || []);
        setTableTotal(tbl.data?.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, pageSize]);

  const handleDateRangeChange = (event, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
    setActivePreset(null);
  };

  const handlePreset = (preset) => {
    const { s, e } = preset.get();
    setStartDate(s.format("YYYY-MM-DD"));
    setEndDate(e.format("YYYY-MM-DD"));
    setActivePreset(preset.label);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFinanceOverviewCsvApi(params);
      saveBlobAsFile(res, `finance-overview-${startDate}_${endDate}.csv`);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const lineChart = useMemo(() => {
    const cats = series.map((r) => r.dateLabel);
    return {
      options: {
        chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.28, opacityTo: 0.02 } },
        xaxis: {
          categories: cats,
          labels: {
            rotate: -45,
            formatter: (v) => {
              if (!v) return "";
              const p = v.split("-");
              return p.length === 3 ? `${MONTH_SHORT[parseInt(p[1]) - 1]} ${String(parseInt(p[2])).padStart(2, "0")}` : v;
            },
          },
          tickAmount: Math.min(cats.length, 12),
        },
        yaxis: { labels: { formatter: (v) => formatInr(v) } },
        colors: ["#1b5a90", "#dc3545", "#e83e8c", "#28a745"],
        legend: { position: "top" },
        tooltip: { y: { formatter: (v) => formatInr(v) } },
        grid: { borderColor: "#f0f0f0", strokeDashArray: 3 },
        dataLabels: { enabled: false },
        markers: { size: 3, hover: { size: 5 } },
      },
      series: [
        { name: "Fees from Users",     data: series.map((r) => Number(r.fee        || 0)) },
        { name: "Fees paid to CoinDCX", data: series.map((r) => Number(r.coinDcxFee || 0)) },
        { name: "Fees paid to KuCoin",  data: series.map((r) => Number(r.kucoinFee  || 0)) },
        { name: "Net Profit",           data: series.map((r) => Number(r.netProfit  || 0)) },
      ],
    };
  }, [series]);

  const donut = useMemo(() => {
    if (!overview) return { options: {}, series: [] };
    const feesFromUsers = Number(overview.feesFromUsers || 0);
    return {
      options: {
        labels: ["Fees from Users", "Fees paid to CoinDCX", "Fees paid to KuCoin"],
        colors: ["#1b5a90", "#dc3545", "#e83e8c"],
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "12px",
          itemMargin: { horizontal: 8, vertical: 4 },
          formatter: (label, opts) => {
            const val = opts.w.globals.series[opts.seriesIndex];
            const total = opts.w.globals.seriesTotals.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
            return `${label} — ${pct}% (${formatInr(val)})`;
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: true, fontSize: "11px", color: "#8a94a6", offsetY: -6 },
                value: {
                  show: true, fontSize: "18px", fontWeight: 700,
                  color: "#1a1a2e", offsetY: 2,
                  formatter: (v) => formatInr(Number(v)),
                },
                total: {
                  show: true, label: "Total Fees",
                  color: "#8a94a6", fontSize: "11px",
                  formatter: () => formatInr(feesFromUsers),
                },
              },
            },
          },
        },
        tooltip: { y: { formatter: (v) => formatInr(v) } },
        dataLabels: { enabled: false },
      },
      series: [
        Number(overview.feesFromUsers    || 0),
        Number(overview.feesPaidToCoinDcx || 0),
        Number(overview.feesPaidToKucoin  || 0),
      ],
    };
  }, [overview]);

  const ColTitle = ({ label, tip }) => (
    <Tooltip title={tip} placement="top">
      <span style={{ cursor: "help", borderBottom: "1px dashed #aab4be" }}>{label}</span>
    </Tooltip>
  );

  const tableColumns = [
    {
      title: "Date", dataIndex: "dateLabel", key: "dateLabel",
      render: (v) => <span style={{ fontWeight: 600, color: "#374151", fontSize: 13 }}>{v}</span>,
    },
    {
      title: <ColTitle label="Total Fees" tip="Total collected from users: Platform Fee + GST + TDS" />,
      key: "totalFees", align: "right",
      render: (_, r) => {
        const total = Number(r.fee || 0) + Number(r.gst || 0) + Number(r.tds || 0);
        return <span style={{ color: "#1b5a90", fontWeight: 700 }}>{formatInr(total)}</span>;
      },
    },
    {
      title: <ColTitle label="Platform Fee" tip="Base 0.42% trading fee — excludes GST and TDS" />,
      key: "fee", align: "right",
      render: (_, r) => <span style={{ color: "#0d6efd", fontWeight: 600 }}>{formatInr(r.fee)}</span>,
    },
    {
      title: <ColTitle label="GST" tip="18% GST on platform fee" />,
      key: "gst", align: "right",
      render: (_, r) => <span style={{ color: "#fd7e14" }}>{formatInr(r.gst)}</span>,
    },
    {
      title: <ColTitle label="TDS" tip="1% TDS on SELL order proceeds" />,
      key: "tds", align: "right",
      render: (_, r) => <span style={{ color: "#20c997" }}>{formatInr(r.tds)}</span>,
    },
    {
      title: <ColTitle label="CoinDCX Fee" tip="Fees paid to CoinDCX for INR pair execution" />,
      key: "coinDcxFee", align: "right",
      render: (_, r) => <span style={{ color: "#dc3545" }}>{formatInr(r.coinDcxFee)}</span>,
    },
    {
      title: <ColTitle label="KuCoin Fee" tip="Fees paid to KuCoin for USDT pair execution" />,
      key: "kucoinFee", align: "right",
      render: (_, r) => <span style={{ color: "#e83e8c" }}>{formatInr(r.kucoinFee)}</span>,
    },
    {
      title: <ColTitle label="Net Profit" tip="Platform Fee minus exchange fees (CoinDCX + KuCoin). GST and TDS are government pass-through, not included." />,
      key: "netProfit", align: "right",
      render: (_, r) => {
        const v = Number(r.netProfit || 0);
        const positive = v >= 0;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, color: positive ? "#28a745" : "#dc3545" }}>
            {positive ? <FiTrendingUp size={13} /> : <FiTrendingDown size={13} />}
            {formatInr(v)}
          </span>
        );
      },
    },
    {
      title: <ColTitle label="Volume" tip="Total notional value of all trades executed on this day" />,
      key: "volume", align: "right",
      render: (_, r) => <span style={{ color: "#6f42c1" }}>{formatInr(r.volume)}</span>,
    },
    {
      title: <ColTitle label="Trades" tip="Number of filled orders on this day" />,
      key: "tradeCount", align: "right",
      render: (_, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4, fontWeight: 600, color: "#0dcaf0" }}>
          {formatNumber(r.tradeCount)}
        </span>
      ),
    },
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* Page header */}
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1">Fee &amp; Revenue Analytics</h3>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0" style={{ fontSize: 13 }}>
                    <li className="breadcrumb-item">
                      <Link to="/admin" className="text-decoration-none">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">Finance Dashboard</li>
                    <li className="breadcrumb-item active" aria-current="page">Fee &amp; Revenue Analytics</li>
                  </ol>
                </nav>

                {/* Rate banner */}
                {overview?.usdtInrRate && (
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: "#eef4fb", border: "1px solid #c8dff5",
                    borderRadius: 8, padding: "9px 16px", marginTop: 10, fontSize: 13,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#2d7dd2" }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>ℹ</span>
                      <span>
                        Live rate: <strong>1 USDT = ₹{Number(overview.usdtInrRate).toFixed(2)}</strong>
                        {" "}— KuCoin (USDT) fees converted to INR at this rate
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#28a745", fontSize: 12, whiteSpace: "nowrap", marginLeft: 12 }}>
                      Updated just now
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#28a745", display: "inline-block" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">

              {/* Row 1 — Revenue overview */}
              <div className="row g-3 mb-3">
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="Total Fees from Users" tip="Total platform fees collected from users: base trading fee (0.42%) + GST (18% of fee) + TDS (1% on SELL orders)" />}
                    value={overview ? formatInr(overview.feesFromUsers) : "-"}
                    color="#1b5a90"
                    breakdown={overview ? { inr: overview.inr?.feesFromUsers, usdt: overview.usdt?.feesFromUsers, inrTotal: overview.feesFromUsers } : null}
                  />
                </div>
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="∑"
                    label={<TipLabel label="Net Profit" tip="Fees collected from users minus exchange execution fees paid to CoinDCX and KuCoin" />}
                    value={overview ? formatInr(overview.netProfit) : "-"}
                    color="#28a745" highlight
                    breakdown={overview ? { inr: overview.inr?.netProfit, usdt: overview.usdt?.netProfit, inrTotal: overview.netProfit } : null}
                  />
                </div>
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="Trading Volume" tip="Total notional value of all executed orders in the selected period (INR pairs in ₹, USDT pairs converted at live rate)" />}
                    value={overview ? formatInr(overview.totalVolume) : "-"}
                    color="#6f42c1"
                    breakdown={overview ? { inr: overview.inr?.totalVolume, usdt: overview.usdt?.totalVolume, inrTotal: overview.totalVolume } : null}
                  />
                </div>
              </div>

              {/* Row 2 — Platform fee components */}
              <div className="row g-3 mb-3">
                {(() => {
                  const pfTotal = Number(overview?.feesFromUsers || 0) - Number(overview?.gstCollected || 0) - Number(overview?.tdsCollected || 0);
                  const pfInr   = Number(overview?.inr?.feesFromUsers || 0) - Number(overview?.inr?.gstCollected || 0) - Number(overview?.inr?.tdsCollected || 0);
                  const pfUsdt  = Number(overview?.usdt?.feesFromUsers || 0) - Number(overview?.usdt?.gstCollected || 0) - Number(overview?.usdt?.tdsCollected || 0);
                  return (
                    <div className="col-xl-4 col-sm-6 col-12">
                      <KpiCard
                        icon="₹"
                        label={<TipLabel label="Platform Fee" tip="Base 0.42% trading fee collected from users, excluding GST and TDS. INR pairs via CoinDCX, USDT pairs via KuCoin." />}
                        value={overview ? formatInr(pfTotal) : "-"}
                        color="#0d6efd"
                        breakdown={overview ? { inr: pfInr, usdt: pfUsdt, inrTotal: pfTotal } : null}
                      />
                    </div>
                  );
                })()}
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="TDS Collected" tip="1% Tax Deducted at Source on SELL order proceeds, collected on behalf of the government. Applies only to sell-side orders." />}
                    value={overview ? formatInr(overview.tdsCollected) : "-"}
                    color="#20c997"
                    breakdown={overview ? { inr: overview.inr?.tdsCollected, usdt: overview.usdt?.tdsCollected, inrTotal: overview.tdsCollected } : null}
                  />
                </div>
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="GST Collected" tip="18% Goods & Services Tax levied on the platform trading fee. Collected from all users on every trade." />}
                    value={overview ? formatInr(overview.gstCollected) : "-"}
                    color="#fd7e14"
                    breakdown={overview ? { inr: overview.inr?.gstCollected, usdt: overview.usdt?.gstCollected, inrTotal: overview.gstCollected } : null}
                  />
                </div>
              </div>

              {/* Row 3 — Exchange costs & trade count */}
              <div className="row g-3 mb-3">
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="CoinDCX Fees" tip="Execution fees paid to CoinDCX exchange for processing INR pair orders on behalf of users" />}
                    value={overview ? formatInr(overview.feesPaidToCoinDcx) : "-"}
                    color="#dc3545"
                    breakdown={overview ? { inr: overview.inr?.feesPaidToCoinDcx, usdt: null, inrTotal: overview.feesPaidToCoinDcx } : null}
                  />
                </div>
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="₹"
                    label={<TipLabel label="KuCoin Fees" tip="Execution fees paid to KuCoin exchange for processing USDT pair orders on behalf of users (converted to INR at live rate)" />}
                    value={overview ? formatInr(overview.feesPaidToKucoin) : "-"}
                    color="#e83e8c"
                    breakdown={overview ? { inr: null, usdt: overview.usdt?.feesPaidToKucoin, inrTotal: overview.feesPaidToKucoin } : null}
                  />
                </div>
                <div className="col-xl-4 col-sm-6 col-12">
                  <KpiCard
                    icon="#"
                    label={<TipLabel label="Total Trades" tip="Number of filled (fully or partially) orders executed in the selected date range" />}
                    value={overview ? formatNumber(overview.tradeCount) : "-"}
                    color="#0dcaf0"
                    footer={overview ? { label: "Total Trades Executed", value: formatNumber(overview.tradeCount), color: "#0dcaf0" } : null}
                  />
                </div>
              </div>

              {/* Charts */}
              <div className="row g-3 mb-3">
                <div className="col-xl-8 col-12">
                  <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                    <div className="card-body">
                      <h6 className="fw-semibold mb-3" style={{ color: "#1a1a2e" }}>Revenue vs Cost over Time</h6>
                      {loading ? (
                        <div className="his-spin"><Spin /></div>
                      ) : series.length === 0 ? (
                        <p className="text-muted">No data for the selected range.</p>
                      ) : (
                        <ReactApexChart options={lineChart.options} series={lineChart.series} type="area" height={300} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-12">
                  <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                    <div className="card-body">
                      <h6 className="fw-semibold mb-3" style={{ color: "#1a1a2e" }}>Fee Composition</h6>
                      {loading || !overview ? (
                        <div className="his-spin"><Spin /></div>
                      ) : (
                        <ReactApexChart options={donut.options} series={donut.series} type="donut" height={340} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily breakdown table */}
              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">

                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>Daily Breakdown</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 3 }}>
                        {loading ? "Loading…" : `${tableTotal} record${tableTotal === 1 ? "" : "s"}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {/* Page size */}
                      <div style={{ position: "relative" }}>
                        <FiList size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none", zIndex: 1 }} />
                        <FiChevronDown size={12} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none", zIndex: 1 }} />
                        <select
                          value={pageSize}
                          onChange={(e) => setPageSize(Number(e.target.value))}
                          style={{ paddingLeft: 28, paddingRight: 24, paddingTop: 8, paddingBottom: 8, fontSize: 13, border: "1.5px solid #e9ecef", borderRadius: 10, background: "#fff", outline: "none", appearance: "none", cursor: "pointer", color: "#374151" }}
                        >
                          {[10, 25, 50, 100].map((n) => (
                            <option key={n} value={n}>{n} / page</option>
                          ))}
                        </select>
                      </div>
                      {/* Export */}
                      <button
                        onClick={handleExport}
                        disabled={exporting}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", background: "#1b5a90", border: "none", borderRadius: 10, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: exporting ? 0.7 : 1 }}
                      >
                        <FiDownload size={14} />
                        {exporting ? <Spinner size="sm" /> : "Export CSV"}
                      </button>
                    </div>
                  </div>

                  {/* Showing info */}
                  {!loading && tableTotal > 0 && (
                    <div style={{ fontSize: 12, color: "#8a94a6", marginBottom: 10 }}>
                      Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, tableTotal)} of {tableTotal} records
                    </div>
                  )}

                  {/* Table */}
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin"><Spin size="large" /></div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={tableColumns}
                        dataSource={tableRows}
                        rowKey={(r) => r.dateLabel}
                        locale={{ emptyText: <div style={{ padding: "32px 0", color: "#888" }}>No data for selected range</div> }}
                      />
                    )}
                    {tableTotal > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={tableTotal}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Filter sidebar */}
            <div className="list-layout__filters" style={{ flexBasis: "230px", width: "230px", minWidth: "210px" }}>

              {/* Main filter card */}
              <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>

                {/* Header */}
                <div style={{ background: "#eef4fb", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: "#dde6f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#1b5a90" }}>
                      <FiFilter size={16} />
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>Filters</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { const init = PRESETS[0].get(); setStartDate(init.s.format("YYYY-MM-DD")); setEndDate(init.e.format("YYYY-MM-DD")); setActivePreset(PRESETS[0].label); }}
                    style={{ width: 32, height: 32, borderRadius: "50%", background: "#dde6f5", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1b5a90" }}
                    title="Reset filters"
                  >
                    <FiRefreshCw size={14} />
                  </button>
                </div>

                {/* Body */}
                <div style={{ padding: "16px" }}>

                  {/* Quick Range */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#8a94a6", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10 }}>Quick Range</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                    {PRESETS.map((p) => {
                      const isActive = activePreset === p.label;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => handlePreset(p)}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            background: isActive ? "#1b5a90" : "#fff",
                            border: `1.5px solid ${isActive ? "#1b5a90" : "#e9ecef"}`,
                            borderRadius: 12, padding: "9px 12px",
                            cursor: "pointer", width: "100%", textAlign: "left",
                            transition: "all 0.15s",
                          }}
                        >
                          <div style={{ width: 30, height: 30, borderRadius: "50%", background: isActive ? "rgba(255,255,255,0.2)" : "#eef4fb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: isActive ? "#fff" : "#1b5a90" }}>
                            <FiCalendar size={14} />
                          </div>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: isActive ? "#fff" : "#1a1a2e" }}>{p.label}</span>
                          {isActive && (
                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <FiCheck size={12} color="#fff" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Date Range */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#8a94a6", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10 }}>Date Range</div>
                  <DateRangePicker
                    key={`drp-${startDate}-${endDate}`}
                    onApply={handleDateRangeChange}
                    initialSettings={{
                      startDate: moment(startDate, "YYYY-MM-DD"),
                      endDate: moment(endDate, "YYYY-MM-DD"),
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8f9fa", border: "1.5px solid #e9ecef", borderRadius: 10, padding: "9px 12px", cursor: "pointer" }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eef4fb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#1b5a90" }}>
                        <FiCalendar size={13} />
                      </div>
                      <span style={{ fontSize: 11, color: "#374151", flex: 1, lineHeight: 1.4 }}>{startDate} → {endDate}</span>
                      <FiChevronDown size={14} color="#8a94a6" />
                    </div>
                  </DateRangePicker>

                  {/* Clear Filters */}
                  <button
                    type="button"
                    onClick={() => { const init = PRESETS[0].get(); setStartDate(init.s.format("YYYY-MM-DD")); setEndDate(init.e.format("YYYY-MM-DD")); setActivePreset(PRESETS[0].label); }}
                    style={{ marginTop: 16, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px 14px", borderRadius: 12, background: "#fff5f5", border: "1.5px solid #fca5a5", color: "#dc2626", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                  >
                    <FiTrash2 size={14} />
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Count card */}
              <div style={{ marginTop: 14, background: "#fff", borderRadius: 16, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#8a94a6", letterSpacing: 1.2, textTransform: "uppercase" }}>Count</span>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: "#eef4fb", display: "flex", alignItems: "center", justifyContent: "center", color: "#1b5a90" }}>
                    <FiBarChart2 size={16} />
                  </div>
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#1a1a2e" }}>{tableTotal}</div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinanceOverview;
